import { MapPin, Calendar, Clock, Building2, Search } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export function Hero() {
  const { t } = useTranslation();
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 bg-grid" aria-hidden />
      <div className="relative mx-auto max-w-4xl px-4 pb-16 pt-12 text-center sm:px-6 lg:px-8 lg:pb-24 lg:pt-20">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-3 py-1 text-xs text-muted-foreground shadow-soft backdrop-blur">
          <span className="flex h-1.5 w-1.5 rounded-full bg-primary" />
          {t("hero.badge")}
        </div>

        <h1 className="mt-6 text-balance font-display text-5xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-6xl lg:text-7xl">
          {t("hero.titleA")}{" "}
          <span className="bg-gradient-to-r from-primary to-[oklch(0.65_0.18_290)] bg-clip-text text-transparent">
            {t("hero.titleB")}
          </span>
        </h1>

        <p className="mx-auto mt-6 max-w-xl text-balance text-lg leading-relaxed text-muted-foreground">
          {t("hero.subtitle")}
        </p>

        {/* Search bar */}
        <div className="mx-auto mt-8 max-w-3xl rounded-2xl border border-border bg-card p-2 shadow-elevated">
          <div className="grid grid-cols-1 divide-y divide-border sm:grid-cols-2 sm:divide-x sm:divide-y-0 lg:grid-cols-3">
            <SearchField icon={MapPin} label={t("hero.where")} placeholder={t("hero.anyCity")} options={KSA_CITIES.map((c) => ({ value: c, label: t(`hero.cityOpt.${c}`, c) }))} />
            <SearchField icon={Calendar} label={t("hero.date")} placeholder={t("hero.addDate")} options={DATE_OPTIONS.map((c) => ({ value: c, label: t(`hero.dateOpt.${c}`, c) }))} />
            <SearchField icon={Building2} label={t("hero.space")} placeholder={t("hero.allTypes")} options={SPACE_TYPES.map((c) => ({ value: c, label: t(`hero.typeOpt.${c}`, c) }))} />
          </div>
          <Link
            to="/discover"
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground shadow-glow transition-all hover:scale-[1.01] hover:opacity-95 active:scale-[0.99]"
          >
            <Search className="h-4 w-4" />
            {t("hero.findSpace")}
          </Link>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm text-muted-foreground">
          <Trust label={t("hero.instant")} />
          <Trust label={t("hero.freeCancel")} />
          <Trust label={t("hero.verified")} />
        </div>
      </div>
    </section>
  );
}

const KSA_CITIES = ["Riyadh", "Jeddah", "Mecca", "Medina", "Dammam", "Khobar", "Dhahran", "Taif", "Tabuk", "Abha"];

const SPACE_TYPES = [
  "Shared Office",
  "Private Office",
  "Meeting Room",
  "Conference Room",
  "Coworking Space",
  "Training Room",
  "Hot Desk",
];

const DATE_OPTIONS = ["Today", "Tomorrow", "This weekend", "Next week", "Pick a date"];

const TIME_OPTIONS = ["Morning (8am – 12pm)", "Afternoon (12pm – 5pm)", "Evening (5pm – 9pm)", "Night (9pm – 12am)"];

function SearchField({
  icon: Icon,
  label,
  placeholder,
  options,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  placeholder: string;
  options?: { value: string; label: string }[];
}) {
  const { i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";
  return (
    <div className="group flex cursor-pointer items-center gap-3 rounded-xl px-4 py-3 transition-colors hover:bg-muted">
      <Icon className="h-5 w-5 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
      <div className="min-w-0 flex-1 text-start">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-foreground/70">{label}</div>
        {options ? (
          <Select defaultValue="" dir={dir}>
            <SelectTrigger aria-label={label} className="h-auto w-full justify-start border-0 bg-transparent p-0 text-start text-sm text-foreground shadow-none ring-0 focus:ring-0 [&>svg]:hidden [&>span]:w-full [&>span]:text-start">
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent align="start" dir={dir} className="text-start">
              {options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value} className="text-start">
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <input
            type="text"
            dir={dir}
            placeholder={placeholder}
            className="w-full bg-transparent text-start text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
          />
        )}
      </div>
    </div>
  );
}


function Trust({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-primary">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="h-3 w-3">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 12l5 5L20 7" />
        </svg>
      </div>
      {label}
    </div>
  );
}

function FloatingCard({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-2xl border border-border bg-card/95 px-3 py-2.5 shadow-card backdrop-blur ${className}`}>
      {children}
    </div>
  );
}
